import { useState } from 'react'
import { api } from '../../api/client'
import type { Kandidat } from './typen'

/**
 * Wer kann diesen Kurs an diesem Tag übernehmen? Der Anwendungsfall bei Krankmeldungen.
 *
 * Zeigt bewusst auch die nicht Verfügbaren mit Grund — eine abgelaufene Untersuchung
 * lässt sich nachholen, ein Urlaub notfalls tauschen.
 */
export function Vertretungssuche({ kursnummern }: { kursnummern: string[] }) {
  const [kurs, setKurs] = useState('')
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10))
  const [kandidaten, setKandidaten] = useState<Kandidat[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function suchen() {
    if (!kurs) return
    setLaeuft(true)
    setFehler(null)
    try {
      setKandidaten(
        await api.get<Kandidat[]>(
          `/api/fahrpersonal/einsetzbar?kursnummer=${encodeURIComponent(kurs)}&datum=${datum}`,
        ),
      )
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Suche fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  const verfuegbar = kandidaten?.filter((k) => k.verfuegbar) ?? []
  const gesperrt = kandidaten?.filter((k) => !k.verfuegbar) ?? []

  return (
    <div className="karte abschnitt" style={{ borderLeft: '4px solid var(--tuerkis-500)' }}>
      <h2>Vertretung suchen</h2>
      <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 6 }}>
        Zeigt alle Mitarbeiter, die für diesen Kurs berechtigt sind — mit Angabe, warum jemand
        ausfällt.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="feld" style={{ marginBottom: 0, minWidth: 180 }}>
          <label htmlFor="kurs">Kurs</label>
          <select id="kurs" value={kurs} onChange={(e) => setKurs(e.target.value)}>
            <option value="">Kurs wählen …</option>
            {kursnummern.map((k) => (
              <option key={k} value={k}>
                Kurs {k}
              </option>
            ))}
          </select>
        </div>
        <div className="feld" style={{ marginBottom: 0 }}>
          <label htmlFor="datum">Einsatztag</label>
          <input
            id="datum"
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
        </div>
        <button className="knopf-primaer" disabled={!kurs || laeuft} onClick={() => void suchen()}>
          {laeuft ? 'Suche läuft …' : 'Suchen'}
        </button>
      </div>

      {fehler && (
        <div className="meldung fehler" style={{ marginTop: 14 }}>
          {fehler}
        </div>
      )}

      {kandidaten && (
        <div style={{ marginTop: 18 }}>
          {kandidaten.length === 0 ? (
            <div className="meldung hinweis">
              Für Kurs {kurs} ist kein Mitarbeiter berechtigt. Ordnen Sie den Kurs einem Team zu
              oder tragen Sie Springer ein.
            </div>
          ) : (
            <>
              <div
                className={`meldung ${verfuegbar.length > 0 ? 'erfolg' : 'fehler'}`}
                style={{ marginBottom: 12 }}
              >
                <strong>{verfuegbar.length}</strong> von {kandidaten.length} berechtigten
                Mitarbeitern können Kurs {kurs} am{' '}
                {datum.split('-').reverse().join('.')} übernehmen.
              </div>

              <div className="tabelle-huelle">
                <table>
                  <thead>
                    <tr>
                      <th>Pers.-Nr.</th>
                      <th>Name</th>
                      <th>Team</th>
                      <th>Berechtigung</th>
                      <th>Verfügbar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...verfuegbar, ...gesperrt].map((k) => (
                      <tr key={k.fahrerId}>
                        <td className="zahl">{k.personalnummer}</td>
                        <td>
                          <strong>{k.name}</strong>
                        </td>
                        <td>{k.teamName ?? '—'}</td>
                        <td>
                          <span className={`etikett ${k.ueberTeam ? 'navy' : 'gruen'}`}>
                            {k.ueberTeam ? 'Stammteam' : 'Springer'}
                          </span>
                        </td>
                        <td>
                          {k.verfuegbar ? (
                            <span className="etikett gruen">ja</span>
                          ) : (
                            <span className="etikett rot" title={k.grund ?? ''}>
                              {k.grund}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
